// ============= Imports =============

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type ReactElement,
  type ReactNode,
} from 'react';
import { UseAuth } from '../../../../../contexts/AuthContext';
import { useFormContext } from '../../../../../contexts/useFormContext';
import { createLogger } from '../../../../../utils/logger';
import { getRejectionReason } from '../../../../../utils/rejectionUtils';
import { FormStackWrapper } from '../FormStackWrapper';
import {
  CANCELLATION_MESSAGE,
  PAYMENT_HOLD_REVIEW_MESSAGE,
} from './constants';
import { RejectionMessage } from './RejectionMessage';
import type { FormSectionProps } from './types';
import { useFormSectionPermissions } from './useFormSectionPermissions';
import { useSectionSchemaRefresh } from './useSectionSchemaRefresh';
import {
  buildRejectionBaseMessage,
  buildRejectionDetailItems,
  isCurrentUserWorkflowCreator,
} from './utils';

// ============= Constants =============

const logger = createLogger('FormSection');

// ============= Component =============

export const FormSection = memo(
  ({
    sectionKey,
    formConfig,
    onSubmit,
    onChange,
    onError,
  }: FormSectionProps): ReactElement => {
    const { formState } = useFormContext();
    const { currentUser } = UseAuth();
    const accordionStatesRef = useRef<Record<string, boolean>>({});

    const {
      isDisabledByPermissions,
      isDisabledByAssignment,
      isDisabledByCreatorCheck,
      permissionsErrorMessage,
      fetchInvolvement,
    } = useFormSectionPermissions({
      sectionKey,
      workflowId: formState.data.id,
      responsibleLawyer: formState.data?.responsible_lawyer,
      lineManager: formState.data?.line_manager,
    });

    const { schemaVersion } = useSectionSchemaRefresh({
      sectionKey,
      formData: formState.data,
    });

    const isSectionComplete =
      formState.sectionProgress?.[sectionKey]?.isComplete;

    useEffect(() => {
      void fetchInvolvement();
    }, [fetchInvolvement]);

    useEffect(() => {
      if (!isDisabledByPermissions) return;
      logger.warning('Form section disabled due to permissions error', {
        section: sectionKey,
        isDisabledByPermissions,
      });
    }, [isDisabledByPermissions, sectionKey]);

    useEffect(() => {
      if (!formState.errors || Object.keys(formState.errors).length === 0) {
        return;
      }
      logger.warning(
        `Form errors for section ${sectionKey}:`,
        formState.errors
      );
    }, [formState.errors, sectionKey]);

    const handleAccordionChange = useCallback(
      (sectionId: string, isOpen: boolean): void => {
        accordionStatesRef.current[sectionId] = isOpen;
      },
      []
    );

    const handleFormError = useCallback(
      (errors: Record<string, string>): void => {
        logger.warning(
          `FormStack validation errors for section ${sectionKey}:`,
          errors
        );
        onError(errors);
      },
      [onError, sectionKey]
    );

    const handleValidSubmit = useCallback(
      async (validFormData: Record<string, unknown>): Promise<void> => {
        await onSubmit(validFormData);
      },
      [onSubmit]
    );

    const workflowId = formState.data?.id;
    const initialDataResetKey = `${sectionKey}:${schemaVersion}:${String(workflowId ?? '')}`;
    const initialDataResetKeyRef = useRef(initialDataResetKey);
    const initialDataRef = useRef(formState.data);

    if (initialDataResetKeyRef.current !== initialDataResetKey) {
      initialDataResetKeyRef.current = initialDataResetKey;
      initialDataRef.current = formState.data;
    }

    const initialData = initialDataRef.current;

    const sectionFormConfig = useMemo(() => {
      if (!formConfig?.formSchema) {
        return undefined;
      }

      const sections =
        typeof formConfig.formSchema === 'string'
          ? []
          : formConfig.formSchema.sections || [];

      return {
        ...formConfig,
        formSchema: {
          sections,
        },
        initialData,
        errors: formState.errors || {},
        onAccordionChange: handleAccordionChange,
      };
    }, [formConfig, formState.errors, handleAccordionChange, initialData]);

    const isCurrentUserCreator = isCurrentUserWorkflowCreator(
      formState.data,
      currentUser
    );

    const rejectionReason = getRejectionReason(formState.data || {});

    const rejectionMessage: ReactNode = formState.isRejected ? (
      <RejectionMessage
        baseMessage={buildRejectionBaseMessage(
          Boolean(isCurrentUserCreator),
          formState.data?.createdByName
        )}
        detailItems={buildRejectionDetailItems(
          formState.data || {},
          rejectionReason
        )}
      />
    ) : null;

    const cancellationMessage = formState.isCancelled
      ? CANCELLATION_MESSAGE
      : null;

    const paymentHoldReviewMessage =
      formState.data?.resp_hold_payment === 'yes'
        ? PAYMENT_HOLD_REVIEW_MESSAGE
        : null;

    if (!sectionFormConfig) {
      return <div>Loading section {sectionKey}...</div>;
    }

    const isDisabled =
      (Boolean(isSectionComplete) && !formState.isRejected) ||
      isDisabledByPermissions ||
      isDisabledByAssignment ||
      isDisabledByCreatorCheck ||
      Boolean(formState.isCancelled);

    return (
      <div key={`form-stack-${sectionKey}-${schemaVersion}`}>
        <FormStackWrapper
          {...sectionFormConfig}
          onChange={onChange}
          onError={handleFormError}
          errors={formState.errors}
          onSubmit={handleValidSubmit}
          disabled={isDisabled}
          sectionKey={sectionKey}
          permissionsErrorMessage={permissionsErrorMessage}
          rejectionMessage={rejectionMessage}
          cancellationMessage={cancellationMessage}
          paymentHoldReviewMessage={paymentHoldReviewMessage}
        />
      </div>
    );
  }
);

FormSection.displayName = 'FormSection';
